<template>
  <div>

    <p class="tab-hint">
      Cada opção corresponde a uma chave do metadata da conta. Chaves fora do catálogo aparecem
      em “Outras chaves” e continuam preservadas ao salvar.
    </p>

    <div v-for="group in groups" :key="group.key" class="group">
      <h4 class="group-title">{{ group.label }}</h4>

      <div class="flags">
        <div v-for="field in group.fields" :key="field.key"
             class="flag"
             :class="{ 'flag-with-hours': hasHours(field), 'marker-yellow': isFlagChanged(field.key) }">

          <div class="flag-main">
            <BkpsCheckBox :value="isEnabled(field.key)" @input="toggle(field.key, $event)">
              <span class="flag-label">{{ field.label }}</span>
            </BkpsCheckBox>

            <p class="flag-description">{{ field.description }}</p>
            <code class="flag-key">{{ field.key }}</code>
          </div>

          <div v-if="hasHours(field) && isEnabled(field.key)" class="flag-hours">
            <div class="hour-field">
              <label :for="`${field.key}-start`">Hora inicial</label>
              <input :id="`${field.key}-start`"
                     :value="metadata[hourKeys.start]"
                     @input="setValue(hourKeys.start, $event.target.value)"
                     type="time" class="form-control">
            </div>

            <div class="hour-field">
              <label :for="`${field.key}-end`">Hora final</label>
              <input :id="`${field.key}-end`"
                     :value="metadata[hourKeys.end]"
                     @input="setValue(hourKeys.end, $event.target.value)"
                     type="time" class="form-control">
            </div>
          </div>

        </div>
      </div>
    </div>

    <div class="group">
      <h4 class="group-title">SNE</h4>

      <SneSettings :account-id="account.id" :metadata="metadata" :pristine-metadata="pristineMetadata" @changed="jsonKey++"/>
    </div>

    <div class="group">
      <h4 class="group-title">
        Outras chaves
        <span class="group-count">{{ others.length }}</span>
      </h4>

      <p v-if="!others.length" class="empty">
        Nenhuma chave fora do catálogo nesta conta.
      </p>

      <div v-for="entry in others" :key="entry.key" class="other-row"
           :class="{ 'marker-yellow': isValueChanged(entry.key) }">
        <code class="other-key">{{ entry.key }}</code>

        <select v-if="isBoolean(entry.value)"
                :value="String(entry.value)"
                @change="setValue(entry.key, $event.target.value === 'true')"
                class="form-control other-input">
          <option value="true">true</option>
          <option value="false">false</option>
        </select>

        <input v-else-if="isEditable(entry.value)"
               :value="entry.value"
               @input="setValue(entry.key, $event.target.value)"
               type="text" class="form-control other-input">

        <span v-else class="other-complex" :title="toJson(entry.value)">
          {{ toJson(entry.value) }}
        </span>

        <button @click="removeOther(entry.key)" class="other-remove" title="Remover chave">
          <x-icon size="1x"/>
        </button>
      </div>
    </div>

    <div class="group">
      <button @click="showJson = !showJson" class="json-toggle">
        <chevron-down-icon size="1x" class="chevron" :class="{ 'chevron-open': showJson }"/>
        Editar JSON bruto
      </button>

      <div v-if="showJson" class="json-body">
        <textarea ref="json" :key="jsonKey" :value="metadataJson"
                  @keydown.tab.prevent="" rows="14" class="form-control json-area"/>

        <div class="json-actions">
          <button @click="resetJson" class="btn btn-outline mr-1">Descartar edição</button>
          <button @click="applyJson" class="btn btn-primary">Aplicar JSON</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
  import { ChevronDownIcon, XIcon } from 'vue-feather-icons'

  import BkpsCheckBox from '@/components/shares/specialTag/BkpsCheckBox'
  import SneSettings from '@/components/admin/accounts/show/SneSettings'
  import {
    HOUR_RANGE_FIELD,
    HOUR_RANGE_KEYS,
    metadataFieldsByGroup,
    uncatalogedEntries
  } from '@/utils/accountMetadataCatalog'

  export default {
    components: { BkpsCheckBox, ChevronDownIcon, SneSettings, XIcon },
    computed: {
      groups() {
        return metadataFieldsByGroup()
      },
      hourKeys() {
        return HOUR_RANGE_KEYS
      },
      metadata() {
        return this.account.metadata
      },
      metadataJson() {
        return this.toJson(this.metadata)
      },
      others() {
        return uncatalogedEntries(this.metadata)
      }
    },
    data() {
      return {
        jsonKey: 0,
        showJson: false
      }
    },
    methods: {
      applyJson() {
        let parsed

        try {
          parsed = JSON.parse(this.$refs.json.value)
        } catch (e) {
          return this.$notify.addError('JSON mal formatado.')
        }

        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
          return this.$notify.addError('O metadata precisa ser um objeto JSON.')

        Object.keys(this.metadata).forEach(key => this.$delete(this.metadata, key))
        Object.keys(parsed).forEach(key => this.$set(this.metadata, key, parsed[key]))

        this.jsonKey++
        this.$notify.add('Metadata atualizado a partir do JSON.')
      },
      hasHours(field) {
        return field.key === HOUR_RANGE_FIELD
      },
      isBoolean(value) {
        return typeof value === 'boolean'
      },
      isEditable(value) {
        return value === null || ['string', 'number'].includes(typeof value)
      },
      isEnabled(key) {
        return !!this.metadata[key]
      },
      isFlagChanged(key) {
        return !!this.metadata[key] !== !!this.pristineMetadata[key]
      },
      isValueChanged(key) {
        return JSON.stringify(this.metadata[key]) !== JSON.stringify(this.pristineMetadata[key])
      },
      removeOther(key) {
        this.$delete(this.metadata, key)
        this.jsonKey++
      },
      resetJson() {
        this.jsonKey++
      },
      setValue(key, value) {
        this.$set(this.metadata, key, value)
        this.jsonKey++
      },
      toggle(key, value) {
        this.setValue(key, value)

        if (this.hasHours({ key: key }) && !value) {
          this.$delete(this.metadata, HOUR_RANGE_KEYS.start)
          this.$delete(this.metadata, HOUR_RANGE_KEYS.end)
        }
      },
      toJson(value) {
        return JSON.stringify(value, null, 2)
      }
    },
    name: 'TabSettings',
    props: {
      account: { required: true, type: Object },
      pristineMetadata: { default: () => ({}), type: Object }
    }
  }
</script>

<style scoped>
  .tab-hint {
    margin-bottom: 24px;
    font-size: 13px;
    color: var(--secondary-text-color);
  }

  .group + .group {
    margin-top: 32px;
    padding-top: 24px;
    border-top: 1px solid var(--border-color);
  }

  .group-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    font-size: 16px;
    font-weight: 600;
    color: var(--primary-text-color);
  }

  .group-count {
    padding: 1px 8px;
    border-radius: 1rem;
    background: var(--light-gray);
    font-size: 12px;
    font-weight: 400;
    color: var(--secondary-text-color);
  }

  .flags {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 24px;
  }

  .flag {
    display: flex;
    align-items: flex-start;
    gap: 16px;
  }

  .flag.marker-yellow,
  .other-row.marker-yellow {
    border-left: solid 5px #FFC866;
    border-radius: 5px 0 0 5px;
    padding-left: 12px;
  }

  .flag-with-hours {
    grid-column: span 2;
  }

  .flag-main {
    flex: 1;
  }

  .flag-label {
    display: inline-flex;
    align-items: center;
    min-height: 16px;
    margin-left: 8px;
    line-height: 16px;
    font-size: 14px;
    font-weight: 600;
    color: var(--primary-text-color);
  }

  .flag-description {
    margin: 2px 0 2px 40px;
    font-size: 12px;
    line-height: 16px;
    color: var(--secondary-text-color);
  }

  .flag-key {
    margin-left: 40px;
    font-size: 11px;
    color: var(--text-muted);
  }

  .flag-hours {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex-shrink: 0;
  }

  .hour-field {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .hour-field label {
    width: 68px;
    margin: 0;
    font-size: 12px;
    text-align: right;
    color: var(--secondary-text-color);
  }

  .hour-field .form-control {
    width: 110px;
    height: 32px;
    font-size: 13px;
  }

  .empty {
    font-size: 13px;
    color: var(--secondary-text-color);
  }

  .other-row {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 8px 0;
    border-bottom: 1px solid var(--border-color);
  }

  .other-key {
    width: 280px;
    flex-shrink: 0;
    font-size: 13px;
    color: var(--primary-text-color);
  }

  .other-input {
    width: 220px;
    height: 34px;
    font-size: 13px;
  }

  .other-complex {
    flex: 1;
    font-family: monospace;
    font-size: 12px;
    color: var(--secondary-text-color);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .other-remove {
    margin-left: auto;
    padding: 2px;
    border: none;
    background: none;
    color: var(--icon-color);
    cursor: pointer;
  }

  .other-remove:hover {
    color: #d9534f;
  }

  .json-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0;
    border: none;
    background: none;
    font-size: 14px;
    font-weight: 600;
    color: var(--primary-text-color);
    cursor: pointer;
  }

  .chevron {
    transition: transform 0.2s ease-in-out;
  }

  .chevron-open {
    transform: rotate(180deg);
  }

  .json-body {
    margin-top: 16px;
  }

  .json-area {
    font-family: monospace;
    font-size: 13px;
  }

  .json-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
  }
</style>
